import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';

import { Spinner } from 'components';
import { Button } from 'components/form';

import { API_ROUTES } from 'lib/api-routes';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { EventAttributes } from 'lib/models/event';
import { TouchpointType } from 'lib/models/touchpoint';
import { TUserApiResponse } from 'lib/models/User.model';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { apiInstance } from 'lib/utils/axios';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';
import { getOpportunityPublicPagePathname } from 'utils/urls';

import Header from '../Header';
import StepHeading from '../StepHeading';
import {
  TouchpointCard,
  TouchpointCartDataProps,
} from '../TouchpointCard/TouchpointCard.component';
import styles from './Step4.module.scss';

interface Step4Props {
  progress: number;
  setFlowState: (flowState: string) => void;
}

export const Step4: React.FC<Step4Props> = ({ progress, setFlowState }) => {
  const { updateUserApiResponse, managerSociety, manager } = useAuth();

  const [t] = useTranslation('onboarding.manager');

  const router = useRouter();
  const [opportunities, setOpportunities] =
    useState<Array<EventAttributes> | null>(null);

  const [isCompleted, setIsCompleted] = React.useState(false);

  useEffect(() => {
    if (managerSociety === null) return;
    const {
      instagram_url,
      facebook_url,
      linkedin_url,
      website,
      student_union_url,
      short_description,
      description,
      banner_url,
      custom_links,
      primary_topic,
      managers,
      stats,
      offerings,
      student_societies_highlights,
    } = managerSociety;

    setIsCompleted(
      !!banner_url &&
        !!description &&
        !!short_description &&
        !!custom_links?.data.length &&
        !!(
          instagram_url ||
          facebook_url ||
          linkedin_url ||
          website ||
          student_union_url
        ) &&
        !!primary_topic &&
        !!stats?.data.length &&
        !!managers?.data.length &&
        !!offerings?.data.length &&
        !!student_societies_highlights?.data.length
    );
  }, [managerSociety]);

  useEffect(() => {
    const opportunitiesPathname = `${API_ROUTES.SOCIETY_TOUCHPOINTS}/similar?touchpointable_types[]=Event&with_details=true`;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    apiInstance.get(opportunitiesPathname).then((res: any) => {
      const list: EventAttributes[] = parseArrayResponse(res);
      setOpportunities(list);
    });
  }, []);

  const handleButton = async (type?: TouchpointType) => {
    if (type) {
      const pathname = `${PAGE_ROUTES.CAMPAIGNS_CREATE}?step=1&type=${type}&onboarding=true&manager=true`;
      router.push(pathname);
    } else {
      const flow_state = ONBOARDING_FLOW_STATE.COMPLETE;

      const manager = { flow_state };
      const userResponse = await apiInstance.patch<TUserApiResponse>(
        API_ROUTES.MANAGER_USER,
        {
          manager,
        }
      );
      updateUserApiResponse(userResponse.data);
      setFlowState(flow_state);
    }
  };

  const getCards = useMemo(() => {
    if (opportunities === null) {
      return (
        <div className={styles.spinner}>
          <Spinner />
        </div>
      );
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const touchpointCards = opportunities.map((campaign: any) => {
        const eventData = parseResponse<{
          icon_url: string;
          start_date: string;
        }>(campaign.touchpointable);
        const opportunity = {
          id: campaign.id,
          title: campaign.title,
          start_date: eventData?.start_date
            ? format(new Date(eventData.start_date), 'MMM d, h:mma')
            : null,
          url: `${window.location.origin}${getOpportunityPublicPagePathname(
            campaign.touchpointable_type,
            campaign.slug
          )}`,
          avatar_urls: campaign.registered_applications_avatar.map(
            (avatar: { avatar_url: string }) => avatar.avatar_url
          ),
          applications_count: campaign.applications,
          event_icon: eventData?.icon_url || '',
        } as TouchpointCartDataProps;

        return <TouchpointCard {...opportunity} key={campaign.id} />;
      });
      return (
        <>
          {touchpointCards}
          <Button
            type="button"
            color="primary"
            className={styles.button}
            onClick={() => handleButton(TouchpointType.Event)}
            isFullWidth
          >
            {t('step-4.create-event')}
          </Button>
        </>
      );
    }
  }, [opportunities]);
  const show_custom_link_screen =
    manager?.linktree_url !== null ||
    managerSociety?.custom_links?.data.length === 0;
  return (
    <>
      <Header
        percentComplete={progress}
        onBack={() => {
          const flow_state = isCompleted
            ? show_custom_link_screen
              ? ONBOARDING_FLOW_STATE.POST_2
              : ONBOARDING_FLOW_STATE.POST_1
            : ONBOARDING_FLOW_STATE.POST_3;
          setFlowState(flow_state);
        }}
      />
      <div className={styles.stepContent}>
        <StepHeading
          title={t('step-4.header.title')}
          desc={t('step-4.header.desc')}
        />

        <div>
          <Button
            type="button"
            color="primary"
            className={styles.button}
            onClick={() => handleButton(TouchpointType.Event)}
            isFullWidth
          >
            {t('step-4.create-event')}
          </Button>

          <Button
            type="button"
            color="secondary"
            className={cn(styles.yesNoButton, styles.button)}
            onClick={() => handleButton()}
            isFullWidth
          >
            {t('do-later')}
          </Button>
        </div>

        <div className={styles.divider}>
          <hr />
        </div>

        <StepHeading desc={t('step-4.header.desc-2')} />

        {getCards}
      </div>
    </>
  );
};
